<template>
  <div class="resume">
    <TheHeader class="resume__header" />
    <div class="resume__content">
      <h4 class="resume__greeting">{{ greetingPhrase }}</h4>
      <p class="resume__greeting-subtitle">Temos os seguintes serviços agendados com você.</p>
      <div class="resume__main">
        <h4>Serviços agendados:</h4>
        <PaymentList :exams="examList" :payment-type="admissionType" />
      </div>
      <div class="resume__footer">
        <div class="amount">
          <span class="amount__label">Valor total</span>
          <span class="amount__value">{{ totalPrice | toCurrency }}</span>
        </div>
        <VButton
          class="resume__action"
          text="Selecionar forma de pagamento"
          variant="primary"
          width="100%"
          @click.native="goToPaymentMethods"
        />
      </div>
    </div>
    <DrawerBottomPayment
      @credit-payment="payWithCreditCard"
      :openDrawer="openDrawer"
      @toggle-drawer="(open) => (openDrawer = open)"
    />
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import TheHeader from '@/components/layout/TheHeader';
import PaymentList from './partials/PaymentList';
import DrawerBottomPayment from './partials/DrawerBottomPayment.partial.vue';
import { PaymentTypeClient } from '@/utils/enums/paymentMagicNumberEnum';
import { dataLayersMixin } from '@/utils/mixins';

export default {
  name: 'PaymentResume',
  mixins: [dataLayersMixin],
  components: {
    PaymentList,
    TheHeader,
    DrawerBottomPayment,
  },
  data() {
    return {
      openDrawer: false,
      examList: [],
    };
  },
  filters: {
    toCurrency(value) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
    },
  },
  computed: {
    ...mapState({
      exams: (state) => state.currentAdmission.data.medicalOrders[0].exams,
      currentAdmission: (state) => state.currentAdmission,
      patient: (state) => state.mdm.data,
      payment: ({ currentAdmission }) => currentAdmission.data.payments[0],
      manuallySelectedExams: ({ searchExams }) => searchExams.data.manuallySelectedExams,
    }),

    ...mapGetters({
      userFirstName: 'global/userFirstName',
      currentBrand: 'selectedBrand',
    }),

    correctGenderSuffix() {
      if (!this.patient.biologicalSex) {
        return 'o';
      }
      return this.patient.biologicalSex === 'MASCULINO' ? 'o' : 'a';
    },
    correctBrand() {
      if (!this.currentBrand) {
        return '';
      }

      return `ao ${this.currentBrand.name.split(' ')[0]}`;
    },
    greetingPhrase() {
      return `Olá, ${this.userFirstName}!`;
    },
    totalPrice() {
      return this.exams.reduce((p, c) => p + c.price, 0);
    },
    admissionType() {
      return this.payment.clientId === PaymentTypeClient.PRIVATE ? 'Particular' : 'Convênio';
    },
    paymentCreditCardLabel() {
      return `
        <CreditCardSvg class="icon" />
        <span>Pagar agora</span>
        (Crédito)
      `;
    },
  },
  methods: {
    payWithCreditCard() {
      this.$router.push({ name: 'credit-card-payment', params: { amount: this.totalPrice } });
    },
    goToPaymentMethods() {
      this.$router.push({ name: 'payment-methods' });
    },
  },
  mounted() {
    this.mostCommonPagesAnalytics({
      eventType: 'funnel_step',
      currentStep: 'Confira seus serviços',
    });

    if (this.manuallySelectedExams.length) {
      this.examList = this.exams.map((exam) => {
        const examSelected = this.manuallySelectedExams.find(
          (item) => item.id.toString() === exam.examCode,
        );
        return {
          ...exam,
          examName: examSelected.name,
        };
      });
    } else {
      this.examList = this.exams;
    }
  },
};
</script>

<style scoped lang="scss">
.resume {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
  height: 100%;
  min-height: calc(100vh - 52px);
  overflow: hidden;

  &__greeting {
    font-weight: 700;
    font-size: 24px;
    line-height: 28.8px;
    color: #161616;
    text-align: start;
    margin-bottom: 16px;
    padding-left: 16px;
    padding-right: 16px;
  }

  &__greeting-subtitle {
    font-size: 16px;
    line-height: 24px;
    color: #161616;
    font-weight: 400;
    text-align: start;
    padding-left: 16px;
    padding-right: 16px;
  }

  &__content {
    margin-top: 20px;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    /* subtrai a altura do footer e header fixos */
    height: calc(100% - 86px);
  }

  &__footer,
  &__header {
    position: fixed;
    width: calc(100%);
  }

  &__header {
    margin: 0 auto;
    top: 0;
  }

  &__main {
    flex: 1;
    display: flex;
    flex-direction: column;
    padding-bottom: 20px;
    overflow-y: auto;
    padding-left: 16px;
    padding-right: 16px;

    h4 {
      text-align: left;
      margin: 32px 0px 16px 0px;
      font-size: 16px;
      color: #363636;
      font-weight: 700;
      line-height: 24px;
    }
  }

  &__amount {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }

  &__footer {
    bottom: 0;
    padding: 16px;
    max-width: 100%;

    background: #ffff;

    button.button > span {
      font-weight: var(--font-weight-bold);
    }
  }

  &__action {
    margin-top: 16px;
    min-width: 100%;
    max-width: 320px;

    &--transparent {
      border-color: transparent;
      background-color: transparent;
      color: var(--color-primary);
    }
  }

  &__credit-card {
    height: calc(100vw / 1.8);
    max-height: 188px;
    max-width: 425px;
    padding: 0 24px;
  }
}
.amount {
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  &__label {
    color: #363636;
    font-size: 16px;
    line-height: 24px;
    font-weight: 700;
  }

  &__value {
    color: var(--color-grey-8);
    font-size: var(--font-size-3);
    font-weight: var(--font-weight-medium);
  }
}

.button {
  border: 0;
  background-color: var(--color-terciary);
  border-radius: 4px;
  height: 48px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #fff;
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-3);
  cursor: pointer;
  transition: background-color 0.3s ease;
  max-width: 320px;

  &:disabled {
    cursor: not-allowed;
    background-color: var(--color-grey-1);
    color: var(--color-grey-5);
  }

  &--big {
    height: 56px;
  }

  &--outline {
    background-color: #fff;
    color: var(--color-terciary);
    border: 1px solid var(--color-terciary);

    &--active {
      background-color: var(--color-terciary);
      color: #ffffff;
    }
  }

  &--no-outline {
    background-color: #fff;
    color: var(--color-primary);
    border: 0;
    text-decoration: underline;
  }
  span {
    margin-right: 7px;
  }
  .icon {
    transform: scale(0.6);

    path {
      fill: #fff;
    }
  }
}
</style>
<style lang="scss">
.payment-resume {
  overflow: hidden;
  height: 100%;
  position: relative;

  .container {
    padding-left: 0px;
    padding-right: 0px;
    padding-bottom: 0px;
    overflow: hidden;
    position: relative;
    height: 100%;
    background-color: #f5f5f5;
  }
}
</style>
